import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen } from "lucide-react";
import { asset } from "@/lib/api";
import { usePdfDoc, usePageImages, useMediaQuery } from "./usePdf";
import { FlipControls, Thumbs } from "./FlipControls";

const pad = (n) => String(n).padStart(2, "0");

const Page = ({ src, n, fit, half }) => (
  <div className={`h-full shrink-0 flex items-center justify-center ${fit ? (half ? "max-w-[50%]" : "max-w-full") : ""}`}>
    {src ? <img src={src} alt={`Page ${n}`} draggable={false}
      className={`block select-none bg-[#0B0C10] shadow-[0_20px_60px_rgba(0,0,0,.6)] ${fit ? "max-h-full max-w-full w-auto h-auto" : "h-full w-auto max-w-none"}`} />
      : <div className="h-full aspect-[3/4] max-w-full grid place-items-center font-mono text-xs text-zinc-400">PAGE {pad(n)}<span className="blink">_</span></div>}
  </div>
);

export const Flipbook = ({ url, title }) => {
  const src = asset(url);
  const { doc, error } = usePdfDoc(src);
  const [images, request] = usePageImages(doc, 2);
  const [thumbs, requestThumb] = usePageImages(doc, 0.3);
  const wide = useMediaQuery("(min-width: 1024px)");
  const [page, setPage] = useState(1);
  const [dir, setDir] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [full, setFull] = useState(false);
  const rootRef = useRef(null);
  const touchX = useRef(null);

  const total = doc?.numPages ?? 0;
  const spread = wide && total > 1;
  const step = spread ? 2 : 1;
  const visible = [page, page + 1].slice(0, step).filter((n) => n <= total);

  useEffect(() => {
    setPage(1);
    setZoom(1);
  }, [doc]);

  useEffect(() => {
    if (spread && page % 2 === 0) setPage(page - 1);
  }, [spread, page]);

  useEffect(() => {
    for (let n = page; n < page + step * 2; n++) request(n);
  }, [page, step, request]);

  useEffect(() => {
    if (!doc) return undefined;
    let stop = false;
    (async () => {
      for (let n = 1; n <= doc.numPages && !stop; n++) await requestThumb(n);
    })();
    return () => { stop = true; };
  }, [doc, requestThumb]);

  const go = useCallback((target) => {
    const t = Math.max(1, Math.min(total, target));
    const aligned = spread ? t - ((t - 1) % 2) : t;
    if (aligned === page) return;
    setDir(aligned > page ? 1 : -1);
    setPage(aligned);
  }, [total, spread, page]);

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowRight") go(page + step);
      if (e.key === "ArrowLeft") go(page - step);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, page, step]);

  useEffect(() => {
    const on = () => setFull(document.fullscreenElement === rootRef.current);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);

  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else if (rootRef.current?.requestFullscreen) rootRef.current.requestFullscreen();
    else window.open(src, "_blank");
  };

  const onTouchEnd = (e) => {
    if (touchX.current == null || zoom > 1) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(page + (dx < 0 ? step : -step));
    touchX.current = null;
  };

  const label = total ? `${visible.map(pad).join("–")} / ${pad(total)}` : "-- / --";

  return (
    <div ref={rootRef} data-testid="pdf-reader" className={`bg-[#F5A524]/40 p-px ${full ? "" : "clip-corner"}`}>
      <div className={`bg-[#12131A] flex flex-col h-full ${full ? "" : "clip-corner"}`}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#F5A524]/20 font-mono text-[11px] tracking-[0.25em]">
          <span className="text-[#F5A524] flex items-center gap-2 truncate"><BookOpen size={14} /> READER // {title}</span>
          <span className="hidden sm:inline text-zinc-400">← → TO FLIP</span>
        </div>

        <div data-lenis-prevent data-testid="flip-stage" onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }} onTouchEnd={onTouchEnd}
          className={`relative overflow-auto bg-[#0B0C10] grid-lines ${full ? "flex-1" : "h-[62vh] md:h-[78vh]"}`} style={{ perspective: 2000 }}>
          {error && (
            <div className="h-full grid place-items-center text-center p-6">
              <a href={src} target="_blank" rel="noreferrer" data-testid="pdf-fallback-link" className="bg-[#F5A524] text-black font-mono font-bold text-sm tracking-[0.2em] px-6 py-4">OPEN PDF IN NEW TAB</a>
            </div>
          )}
          {!doc && !error && <p className="h-full grid place-items-center font-mono text-xs tracking-[0.3em] text-zinc-400">DECRYPTING PAGES<span className="blink">_</span></p>}
          {doc && (
            <div className={`grid min-w-full ${zoom === 1 ? "w-full" : "w-max"}`} style={{ height: `${zoom * 100}%`, gridTemplateRows: "100%", gridTemplateColumns: zoom === 1 ? "100%" : "minmax(100%, max-content)" }}>
              <AnimatePresence initial={false} custom={dir}>
                <motion.div key={page} custom={dir} className="col-start-1 row-start-1 flex justify-center items-center gap-1 p-3 sm:p-6 h-full w-full min-w-0"
                  style={{ transformOrigin: dir > 0 ? "left center" : "right center" }}
                  initial={{ rotateY: dir * 75, opacity: 0, x: dir * 40 }}
                  animate={{ rotateY: 0, opacity: 1, x: 0 }}
                  exit={{ rotateY: dir * -75, opacity: 0, x: dir * -40 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
                  {visible.map((n) => <Page key={n} n={n} src={images[n]} fit={zoom === 1} half={visible.length > 1} />)}
                </motion.div>
              </AnimatePresence>
            </div>
          )}
        </div>

        {doc && (
          <>
            <FlipControls label={label} canPrev={page > 1} canNext={page + step <= total} onPrev={() => go(page - step)} onNext={() => go(page + step)}
              zoom={zoom} setZoom={setZoom} full={full} toggleFull={toggleFull} src={src} />
            <Thumbs count={total} thumbs={thumbs} visible={visible} onPick={go} />
          </>
        )}
      </div>
    </div>
  );
};
